import type { OwnerInsight } from '@/infrastructure/http/metricsApi';
import { canAccessClinicHub } from '@/application/navigation/menuHouses';

/** Destinos accionables del bloque Atención (US-011). */
export type AttentionCtaTarget = 'clinic' | 'finances' | 'professionals' | 'patients';

const FINANCES_KEYS = ['metrics.caja', 'metrics.tendencia', 'metrics.ranking_planes'] as const;
const PATIENTS_KEYS = ['metrics.pacientes', 'metrics.recurrencia', 'metrics.no_volvieron'] as const;
const PROFESSIONALS_KEYS = ['metrics.profesionales'] as const;

export function canAccessFinancesCta(hasPermission: (key: string) => boolean): boolean {
  return FINANCES_KEYS.some((key) => hasPermission(key));
}

export function canAccessPatientsCta(hasPermission: (key: string) => boolean): boolean {
  return PATIENTS_KEYS.some((key) => hasPermission(key));
}

export function canAccessProfessionalsCta(hasPermission: (key: string) => boolean): boolean {
  return PROFESSIONALS_KEYS.some((key) => hasPermission(key));
}

export function canAccessAttentionCta(
  cta: AttentionCtaTarget,
  hasPermission: (key: string) => boolean
): boolean {
  if (cta === 'finances') return canAccessFinancesCta(hasPermission);
  if (cta === 'patients') return canAccessPatientsCta(hasPermission);
  if (cta === 'professionals') return canAccessProfessionalsCta(hasPermission);
  return canAccessClinicHub(hasPermission);
}

/** Normaliza CTA del server (incl. overview legacy → patients). */
export function mapServerInsightCta(insight: OwnerInsight): AttentionCtaTarget {
  if (insight.type === 'absenteeism_high') return 'patients';
  if (insight.type === 'revenue_drop') return 'finances';
  if (insight.type === 'pvp_drop') {
    return insight.cta === 'professionals' ? 'professionals' : 'clinic';
  }
  if (insight.cta === 'finances') return 'finances';
  if (insight.cta === 'professionals') return 'professionals';
  if (insight.cta === 'patients' || insight.cta === 'overview') return 'patients';
  return 'clinic';
}

/**
 * CTA preferido por clínica a partir de sus insights.
 * Prioridad: ingresos (cualquier severidad) → PVP → ausentismo → hub.
 * Si hay revenue_drop en la fila, Finanzas gana aunque también haya PVP.
 */
export function preferredAttentionCta(insights: OwnerInsight[]): AttentionCtaTarget {
  if (insights.some((i) => i.type === 'revenue_drop')) {
    return 'finances';
  }
  if (insights.some((i) => i.type === 'pvp_drop')) {
    const pvp = insights.find((i) => i.type === 'pvp_drop');
    return pvp ? mapServerInsightCta(pvp) : 'professionals';
  }
  if (insights.some((i) => i.type === 'absenteeism_high')) return 'patients';
  if (insights.length === 1) return mapServerInsightCta(insights[0]);
  return 'clinic';
}

/** Fallback si el CTA preferido no tiene permiso. */
export function resolveAccessibleAttentionCta(
  preferred: AttentionCtaTarget,
  hasPermission: (key: string) => boolean
): AttentionCtaTarget | null {
  const fallbacks: AttentionCtaTarget[] =
    preferred === 'finances'
      ? ['finances', 'clinic']
      : preferred === 'patients'
        ? ['patients', 'clinic']
        : preferred === 'professionals'
          ? ['professionals', 'clinic']
          : ['clinic'];

  for (const cta of fallbacks) {
    if (canAccessAttentionCta(cta, hasPermission)) return cta;
  }
  return null;
}

export function buildAttentionHref(opts: {
  cta: AttentionCtaTarget;
  tenantId: string;
  period: string;
  hideClinicName?: boolean;
  /** Fuerza tab en /patients (ej. risk desde Top 3 del hub). */
  patientsTab?: 'overview' | 'acquisition' | 'retention' | 'risk';
  /** Sucursal (consultorioId) cuando el hub filtra por sede. */
  sede?: number;
}): string {
  const { cta, tenantId, period, hideClinicName = false, patientsTab, sede } = opts;
  const periodQs = `period=${encodeURIComponent(period)}`;
  const sedeQs = sede != null && Number.isFinite(sede) ? `&sede=${encodeURIComponent(String(sede))}` : '';
  const hub = `/clinics/${encodeURIComponent(tenantId)}?${periodQs}${sedeQs}`;

  if (cta === 'finances') {
    // Dominio Finanzas ya filtrado (tenant + periodo + overview) — multi y mono.
    return `/finances?${periodQs}&tenant=${encodeURIComponent(tenantId)}&tab=overview${sedeQs}`;
  }
  if (cta === 'professionals') {
    return `${hub}&tab=professionals`;
  }
  if (cta === 'patients') {
    const tab = patientsTab ?? (hideClinicName ? 'risk' : null);
    if (tab) {
      return `/patients?${periodQs}&tenant=${encodeURIComponent(tenantId)}&tab=${tab}${sedeQs}`;
    }
    return `${hub}&tab=patients`;
  }
  return hub;
}

export function clinicHubHref(tenantId: string, period: string, sede?: number): string {
  const sedeQs = sede != null && Number.isFinite(sede) ? `&sede=${encodeURIComponent(String(sede))}` : '';
  return `/clinics/${encodeURIComponent(tenantId)}?period=${encodeURIComponent(period)}${sedeQs}`;
}
